import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Link} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';
import moment from 'moment';
import {
    DndContext,
    closestCenter,
    useSensor,
    useSensors,
    PointerSensor,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {CSS} from '@dnd-kit/utilities';

import {Spinner} from '../Spinner';
import {FaIcon} from '../FaIcon';
import {InformesApi, isOk, listOf} from '../../api/informesApi';
import {usePortalSubscriptionGating} from '../../utils/usePortalSubscriptionGating';
import {clientInformesPath} from '../informes/informesPaths';
import {notifyOnboardingProgress} from '../../utils/onboardingProgress';

function InformeRow({informe, clientId, isDraggable, t, onAction, canPublish}) {
    const {attributes, listeners, setNodeRef, transform, transition, isDragging} =
        useSortable({id: informe.id, disabled: !isDraggable});

    const style = isDraggable
        ? {
            transform: CSS.Transform.toString(transform),
            transition: isDragging ? 'none' : transition,
            opacity: isDragging ? 0.6 : 1,
        }
        : {};

    const meta = [
        informe.mode === 'SNAPSHOT'
            ? t('portal_informes.mode_snapshot')
            : t('portal_informes.mode_live'),
        informe.source_template_name || null,
        informe.updated_on
            // `escapeValue: false` o las barras de la fecha salen como `&#x2F;` en pantalla:
            // `i18n.js` no lo desactiva globalmente y el default de i18next escapa la barra.
            ? t('portal_informes.meta_updated', {
                date: moment(informe.updated_on).format('DD/MM/YYYY'),
                interpolation: {escapeValue: false},
            })
            : null,
    ].filter(Boolean).join(' · ');

    return (
        <div ref={setNodeRef} style={style} className="portal-reports-row">
            {isDraggable && (
                <span
                    className="portal-reports-row-handle"
                    {...attributes}
                    {...listeners}
                    title={t('portal_reports.drag_handle')}
                >
                    <FaIcon iconName="grip-vertical"/>
                </span>
            )}

            {informe.is_published && (
                <span className="portal-reports-row-order">
                    #{(informe.portal_order ?? 0) + 1}
                </span>
            )}

            <div className="portal-reports-row-main">
                <div className="portal-reports-row-name-row">
                    <span className="portal-reports-row-name">{informe.name}</span>
                    {informe.is_published && (
                        <span className="saved-view-portal-badge">
                            {t('informes.pill.published')}
                        </span>
                    )}
                </div>
                {informe.description && (
                    <div className="portal-reports-row-desc">{informe.description}</div>
                )}
                <div className="portal-reports-row-meta">{meta}</div>
            </div>

            <div className="portal-reports-row-actions">
                {informe.is_published ? (
                    <button
                        type="button"
                        className="portal-reports-row-unassign-btn"
                        onClick={() => onAction('unpublish', informe)}
                        title={t('informes.header.unpublish')}
                    >
                        <FaIcon iconName="xmark"/>
                        {t('portal_reports.action_unassign_short')}
                    </button>
                ) : (
                    <button
                        type="button"
                        className="portal-reports-row-assign-btn"
                        onClick={() => onAction('publish', informe)}
                        disabled={!canPublish}
                        title={canPublish ? t('informes.header.publish') : t('portal_reports.upgrade_hint')}
                    >
                        <FaIcon iconName="bookmark"/>
                        {t('portal_informes.action_publish_short')}
                    </button>
                )}
                <Link
                    to={clientInformesPath(clientId, informe.id)}
                    className="portal-reports-row-open-link"
                    title={t('portal_informes.action_open')}
                >
                    <FaIcon iconName="arrow-up-right-from-square"/>
                    {t('portal_reports.action_open_short')}
                </Link>
            </div>
        </div>
    );
}

/**
 * Sección embebible que gestiona qué INFORMES (app `reports`, modalidad
 * `REPORT_WITH_TEMPLATES`) están asociados al portal del cliente. Es la hermana de
 * `ClientPortalReportsSection`, que hace lo mismo con las vistas del Explorador en la
 * modalidad `DATA_EXPLORER`. Sin layout propio: sólo el contenido del panel.
 *
 * Existe porque el panel de este módulo montaba el selector de plantillas de reportes V1
 * (`PortalTemplatePicker`) al ver la modalidad `REPORT_WITH_TEMPLATES`: ese valor de enum
 * significaba «reporte por plantilla en el portal» ANTES del frente Informes, que lo reusó
 * con otro sentido. La agencia en modalidad Informes se quedaba sin forma de asociarlos.
 */
export function ClientPortalInformesSection({clientId, onCountChange, onPublished}) {
    const {t} = useTranslation();
    const {canAssignToPortal, loading: gatingLoading} = usePortalSubscriptionGating();

    const [loading, setLoading] = useState(true);
    const [informes, setInformes] = useState([]);
    const sensors = useSensors(useSensor(PointerSensor, {activationConstraint: {distance: 6}}));

    const fetchInformes = useCallback(() => {
        if (!clientId) {
            setLoading(false);
            return;
        }
        setLoading(true);
        InformesApi.list(clientId).then((r) => {
            setLoading(false);
            if (!isOk(r)) {
                toast.error(t('portal_informes.load_error'));
                return;
            }
            setInformes(listOf(r, 'client_reports'));
        }).catch(() => {
            setLoading(false);
            toast.error(t('portal_informes.load_error'));
        });
    }, [clientId, t]);

    useEffect(() => { fetchInformes(); }, [fetchInformes]);

    const published = useMemo(
        () => informes
            .filter((i) => i.is_published)
            .sort((a, b) => (a.portal_order ?? 0) - (b.portal_order ?? 0)),
        [informes]
    );
    const others = useMemo(() => informes.filter((i) => !i.is_published), [informes]);

    // La cantidad publicada alimenta el resumen de la card del módulo.
    useEffect(() => {
        if (onCountChange) onCountChange(published.length);
    }, [published.length, onCountChange]);

    const handleAction = (action, informe) => {
        if (action === 'publish') {
            if (!canAssignToPortal) {
                toast.info(t('portal_reports.upgrade_hint'));
                return;
            }
            InformesApi.publish(informe.id).then((r) => {
                if (!isOk(r)) {
                    toast.error(t('informes.publish_error'));
                    return;
                }
                toast.success(r.data && r.data.snapshot_requested
                    ? t('informes.published_snapshot')
                    : t('informes.published'));
                // Asociarlo cumple «Comparte un informe en el portal» de Aprende Dashbo, igual que desde el
                // editor (`InformesPage`): se tilda en vivo (review C-apr-guias-01, ronda 2). Quitarlo no.
                notifyOnboardingProgress(r);
                fetchInformes();
                // Publicar prende el módulo Reportes del portal en el BACKEND: el dueño de la
                // card re-lee la config para no seguir mostrando "Desactivado" hasta un F5.
                if (onPublished) onPublished();
            });
            return;
        }
        if (action === 'unpublish') {
            InformesApi.unpublish(informe.id).then((r) => {
                if (!isOk(r)) {
                    toast.error(t('informes.publish_error'));
                    return;
                }
                toast.success(t('informes.unpublished'));
                fetchInformes();
            });
        }
    };

    const handleDragEnd = (event) => {
        const {active, over} = event;
        if (!over || active.id === over.id) return;
        const oldIndex = published.findIndex((i) => i.id === active.id);
        const newIndex = published.findIndex((i) => i.id === over.id);
        if (oldIndex < 0 || newIndex < 0) return;

        const reordered = arrayMove(published, oldIndex, newIndex);
        setInformes(informes.map((i) => {
            const index = reordered.findIndex((r) => r.id === i.id);
            return index >= 0 ? {...i, portal_order: index} : i;
        }));

        InformesApi.reorderPublished(clientId, reordered.map((i) => i.id)).then((r) => {
            if (!isOk(r)) {
                toast.error(t('portal_informes.reorder_error'));
                fetchInformes();
            }
        });
    };

    const renderContent = () => {
        if (loading) {
            return <div className="portal-reports-loading"><Spinner/></div>;
        }
        if (informes.length === 0) {
            return (
                <div className="portal-reports-empty">
                    <div className="saved-view-modal-eyebrow">{t('portal_informes.config_empty_eyebrow')}</div>
                    <p>{t('portal_informes.config_empty_body')}</p>
                    <Link
                        to={clientInformesPath(clientId)}
                        className="btn -alt portal-reports-empty-cta"
                    >
                        {t('portal_informes.cta_create')}
                    </Link>
                </div>
            );
        }

        return (
            <>
                <div className="portal-reports-section">
                    <div className="portal-reports-section-title">
                        {t('portal_informes.section_published')} · {published.length}
                    </div>
                    {published.length === 0 ? (
                        <div className="portal-reports-empty-section portal-reports-empty-assigned">
                            <FaIcon iconName="arrow-down"/>
                            <span>{t('portal_informes.empty_published_hint')}</span>
                        </div>
                    ) : (
                        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                            <SortableContext
                                items={published.map((i) => i.id)}
                                strategy={verticalListSortingStrategy}
                            >
                                {published.map((informe) => (
                                    <InformeRow
                                        key={informe.id}
                                        informe={informe}
                                        clientId={clientId}
                                        isDraggable
                                        t={t}
                                        onAction={handleAction}
                                        canPublish={canAssignToPortal}
                                    />
                                ))}
                            </SortableContext>
                        </DndContext>
                    )}
                </div>

                <div className="portal-reports-section">
                    <div className="portal-reports-section-title -muted">
                        {t('portal_informes.section_others')} · {others.length}
                    </div>
                    {others.length === 0 ? (
                        <div className="portal-reports-empty-section">
                            {t('portal_informes.no_others_yet')}
                        </div>
                    ) : (
                        others.map((informe) => (
                            <InformeRow
                                key={informe.id}
                                informe={informe}
                                clientId={clientId}
                                isDraggable={false}
                                t={t}
                                onAction={handleAction}
                                canPublish={canAssignToPortal}
                            />
                        ))
                    )}
                </div>
            </>
        );
    };

    return (
        <div className="portal-reports-section-wrap" data-testid="portal-informes-section">
            {!gatingLoading && !canAssignToPortal && (
                <div className="portal-reports-subscription-banner">
                    <div className="portal-reports-subscription-text">
                        <span className="saved-view-modal-eyebrow">{t('portal_reports.upgrade_eyebrow')}</span>
                        <p className="portal-reports-subscription-hint">{t('portal_reports.upgrade_hint')}</p>
                    </div>
                    <a href="/agency/subscription" className="btn -alt">
                        <FaIcon iconName="arrow-up-right-from-square"/> {t('saved_views.upgrade_cta')}
                    </a>
                </div>
            )}

            <div className="portal-reports-header-row">
                <div className="portal-reports-header-text">
                    <p className="portal-reports-header-hint">{t('portal_informes.header_hint')}</p>
                </div>
                <div className="portal-reports-header-actions">
                    <Link
                        to={clientInformesPath(clientId)}
                        className="btn -alt portal-reports-create-btn"
                    >
                        <FaIcon iconName="plus"/> {t('portal_informes.cta_create')}
                    </Link>
                </div>
            </div>

            {renderContent()}
        </div>
    );
}
